import { clamp, sum, sumBy } from "lodash";
import { DateTime } from "luxon";

import type { EnumValue } from "@storyboard/dispatch-view/fieldKinds";
import type { DispatchColor } from "@storyboard/dispatch-view/view";
import type { RouteForecast } from "@storyboard/web-client";
import { formatItemTime } from "@/components/Dispatch/format";
import type { DriverRouteSummary, RouteRestSummary, RouteStopState, RouteStopSummary } from "@/types/driverRoute";
import { formatLocalDate, formatZonedDate } from "@/utils/dateFormat";

/** How a stop's projected arrival stands against its appointment, as a pill: green on time, red late, amber early. */
export const ARRIVAL_RISK_VALUES: readonly EnumValue[] = [
  { value: "onTime", label: "On time", color: "green" },
  { value: "early", label: "Early", color: "amber" },
  { value: "late", label: "Late", color: "red" },
  { value: "unknown", label: "No appointment", color: "zinc" },
];

/** Where a stop stands, as a pill: the driver's progress reads down the route by color before the words are read. */
export const STOP_STATE_VALUES: readonly EnumValue[] = [
  { value: "scheduled", label: "Scheduled", color: "zinc" },
  { value: "enRoute", label: "En route", color: "blue" },
  { value: "arrived", label: "Arrived", color: "teal" },
  { value: "departed", label: "Departed", color: "green" },
  { value: "skipped", label: "Skipped", color: "amber" },
  { value: "bypassed", label: "Bypassed", color: "amber" },
  { value: "unassigned", label: "Unassigned", color: "zinc" },
  { value: "unknown", label: "Unknown", color: "zinc" },
];

/** The pill definition for an enum value, so an unlisted value still renders as its wire name. */
export function knownValue(values: readonly EnumValue[], value: string): EnumValue | undefined {
  return values.find((known) => known.value === value);
}

/** What a stop is for, as the vendor categorized it, in the dispatcher's words. */
const STOP_ROLE_LABELS: Record<NonNullable<RouteStopSummary["role"]>, string> = {
  pickup: "Pickup",
  delivery: "Delivery",
  stop: "Stop",
  fuel: "Fuel",
  trailerInterchange: "T-call",
};

/**
 * Whether a stop of this role is one an appointment could be kept at.
 *
 * A fuel stop never has one, and the forecast never raises `noAppointment` for it, so its `unknown` risk means only
 * "nothing to judge against" — reading it as "No appointment" beside a fuel stop tells the dispatcher nothing. The wire
 * has no waypoint role; a vendor waypoint arrives as fuel or plain stop.
 */
export function keepsAppointments(role: RouteStopSummary["role"]): boolean {
  return role !== "fuel";
}

/** The dispatcher-facing word for a stop's role, `T-call` for a trailer interchange; empty when the vendor stated none. */
export function formatStopRole(stop: Pick<RouteStopSummary, "role">): string {
  return stop.role ? STOP_ROLE_LABELS[stop.role] : "";
}

/**
 * How the freight moves at a stop, as the TMS coded it, in the dispatcher's words.
 *
 * Total rather than partial, so a value added to the wire cannot compile until it has a word here.
 */
const STOP_HANDLING_LABELS: Record<NonNullable<RouteStopSummary["handling"]>, string> = {
  live: "Live",
  driverAssist: "Drv assist",
  preload: "Preload",
  drop: "Drop",
  window: "Window",
};

/**
 * The dispatcher-facing word for how the freight moves at a stop; empty when the vendor stated none.
 */
export function formatStopHandling(stop: Pick<RouteStopSummary, "handling">): string {
  return stop.handling ? STOP_HANDLING_LABELS[stop.handling] : "";
}

/**
 * Whether a stop's handling word is set in heavier type: a driver-assist stop, which holds the driver while the freight
 * moves. A live stop holds them too, and stands out as a chip instead; a drop, window or preload stop lets them drop
 * and go.
 */
export function emphasizesHandling(handling: RouteStopSummary["handling"]): boolean {
  return handling === "driverAssist";
}

/** The Next Stop column's marker for a stop's role: a letter, its dispatcher word, and its color. */
export type StopRoleMarker = { letter: string; label: string; color: DispatchColor };

/**
 * Each role's Next Stop marker, in the words a dispatcher uses for the load (shipper and consignee) rather than the
 * Context tab's pickup and delivery; a plain stop carries none.
 */
const STOP_ROLE_MARKERS: Record<NonNullable<RouteStopSummary["role"]>, StopRoleMarker | null> = {
  pickup: { letter: "S", label: "Shipper", color: "amber" },
  delivery: { letter: "C", label: "Consignee", color: "green" },
  fuel: { letter: "F", label: "Fuel stop", color: "orange" },
  trailerInterchange: { letter: "T", label: "T-call", color: "blue" },
  stop: null,
};

/** The Next Stop column's marker for a stop's role; null for a plain stop and when the vendor stated no role. */
export function stopRoleMarker(role: RouteStopSummary["role"]): StopRoleMarker | null {
  return role ? STOP_ROLE_MARKERS[role] : null;
}

/**
 * Whether a stop of this role shows an ETA. A fuel stop's is only a sequencing time: the forecast charges no miles to
 * reach it.
 */
export function showsEta(role: RouteStopSummary["role"]): boolean {
  return role !== "fuel";
}

/**
 * The stop's place as "City, ST", from the fields the vendor stated rather than a parse of the address line; null
 * unless it stated both.
 */
export function formatStopPlace(stop: Pick<RouteStopSummary, "city" | "region">): string | null {
  return stop.city && stop.region ? `${stop.city}, ${stop.region}` : null;
}

type Appointment = RouteStopSummary["appointment"];

/** How a time is placed on the page: the zone to read it in, and the dispatcher's, to say when the two differ. */
export type AppointmentZone = {
  /** The IANA zone the stop keeps; null reads the time in the dispatcher's own zone. */
  timeZone: string | null;
  /** The dispatcher's zone; the browser's unless a test pins it. */
  dispatcherZone?: string;
  /** What "today" is judged against; the clock unless a test pins it. */
  now?: Date;
};

/** The dispatcher's IANA zone, as the browser reports it. */
function browserZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/** An instant as the row prints it: its day ("MMM d"), null on today, its "HH:mm", and the instant itself. */
type Clock = { day: string | null; time: string; at: Date };

/** Whether the browser can read times in the named zone. */
function isKnownZone(timeZone: string): boolean {
  try {
    Intl.DateTimeFormat(undefined, { timeZone });
    return true;
  } catch {
    return false;
  }
}

/**
 * An instant on the stop's clock, its day stated only when the date shown there is not the dispatcher's today.
 *
 * Judged against the dispatcher's date rather than the stop zone's, so a bare time always means the dispatcher's today:
 * near midnight a stop an hour behind can still be on yesterday while the dispatcher is on today. Reads the
 * dispatcher's clock when the stop names no zone or one the browser does not know.
 */
function clockIn(iso: string, zone: AppointmentZone): Clock | null {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  const today = formatZonedDate(zone.now ?? new Date(), zone.dispatcherZone ?? browserZone(), "yyyy-MM-dd");
  const read = (pattern: string) =>
    zone.timeZone && isKnownZone(zone.timeZone)
      ? formatZonedDate(date, zone.timeZone, pattern)
      : formatLocalDate(date, pattern);
  const day = read("yyyy-MM-dd") === today ? null : read("MMM d");
  return { day, time: read("HH:mm"), at: date };
}

/** A clock as words, its day first when it has one. */
function clockWords(clock: Clock): string {
  return clock.day ? `${clock.day}, ${clock.time}` : clock.time;
}

/**
 * The stop zone's abbreviation at an instant when it keeps a different clock from the dispatcher's, else null.
 *
 * Compared by offset rather than name, so two names for one clock (`US/Central`, `America/Chicago`) read as the same.
 */
function zoneNoteAt(at: Date, zone: AppointmentZone): string | null {
  if (!zone.timeZone || !isKnownZone(zone.timeZone)) return null;
  // An empty dispatcher zone reads as UTC, as date-fns-tz read it.
  const dispatcherZone = zone.dispatcherZone ?? browserZone();
  const onStopClock = DateTime.fromJSDate(at, { zone: zone.timeZone });
  if (onStopClock.offset === DateTime.fromJSDate(at, { zone: dispatcherZone || "utc" }).offset) return null;
  // The zone's short name in the viewer's own language, as Intl gives it, unlike the English the clocks are read in.
  return onStopClock.toFormat("ZZZZ");
}

/** A clock as words with the zone note it was read under, when there is one. */
function notedWords(words: string, note: string | null): string {
  return note ? `${words} ${note}` : words;
}

/**
 * The appointment as the row states it, in the stop's zone: "appt HH:mm–HH:mm" for a window, "appt HH:mm" for a
 * single time (start equal to end), or its one stated bound as "appt by" / "appt from"; null with none.
 *
 * A time not on the zone's today carries its day, and a window inside one day states that day once. The zone's
 * abbreviation follows when it keeps a different clock from the dispatcher's, so "appt 08:00 MDT" is never read as the
 * dispatcher's eight o'clock. Each note is read at the instant it labels, so a window across a DST change names both.
 */
export function formatAppointment(appointment: Appointment, zone: AppointmentZone = { timeZone: null }): string | null {
  if (!appointment) return null;
  const from = appointment.start ? clockIn(appointment.start, zone) : null;
  const to = appointment.end ? clockIn(appointment.end, zone) : null;
  if (from && to) {
    const fromNote = zoneNoteAt(from.at, zone);
    if (from.at.getTime() === to.at.getTime()) return notedWords(`appt ${clockWords(from)}`, fromNote);
    const toNote = zoneNoteAt(to.at, zone);
    const until = from.day === to.day ? to.time : clockWords(to);
    if (fromNote === toNote) return notedWords(`appt ${clockWords(from)}–${until}`, toNote);
    return `appt ${notedWords(clockWords(from), fromNote)}–${notedWords(until, toNote)}`;
  }
  if (to) return notedWords(`appt by ${clockWords(to)}`, zoneNoteAt(to.at, zone));
  if (from) return notedWords(`appt from ${clockWords(from)}`, zoneNoteAt(from.at, zone));
  return null;
}

/**
 * When a stop still ahead is reached, as its row words it: "arrived HH:mm" once the forecast observed the arrival, else
 * "ETA HH:mm"; null without an ETA, and for a fuel stop (see {@link showsEta}).
 *
 * Beside an appointment, pass the appointment's zone: the time is read on the stop's clock with the same zone note, so
 * "ETA 14:09 EDT" checks against "appt 07:00–14:00 EDT" and its pill without arithmetic. Alone, it reads on the
 * dispatcher's clock. Either way it carries its date when it is not the dispatcher's today.
 */
export function formatArrival(
  stop: Pick<RouteStopSummary, "eta" | "arrivalObserved" | "role">,
  zone: AppointmentZone = { timeZone: null },
): string | null {
  const clock = stop.eta ? clockIn(stop.eta, zone) : null;
  if (!clock) return null;
  const time = notedWords(clockWords(clock), zoneNoteAt(clock.at, zone));
  if (stop.arrivalObserved) return `arrived ${time}`;
  return showsEta(stop.role) ? `ETA ${time}` : null;
}

/**
 * The arrival risk worth a pill on a stop, or null where a pill would say something untrue.
 *
 * Nothing once the driver is past the stop, and nothing on a fuel stop (see {@link keepsAppointments}). `unknown` is the forecast reporting it had no window to judge against,
 * which the pill spells "No appointment" — so where the stop shows the vendor's own window instead, the pill is dropped
 * rather than contradicting the line beside it.
 */
export function shownRisk(stop: RouteStopSummary): RouteStopSummary["risk"] {
  if (stop.done || !keepsAppointments(stop.role)) return null;
  if (stop.risk === "unknown" && formatAppointment(stop.appointment) !== null) return null;
  return stop.risk;
}

/**
 * The state a stop's pill shows: `arrived` once the forecast observed the driver reach a stop the vendor still calls en
 * route, else the vendor's own state.
 *
 * Platform Science never reports `arrived`, so a checked-in driver would otherwise read "En route" beside "arrived
 * HH:mm". No other state is overridden: the vendor's word on a departed, skipped, or bypassed stop stands.
 */
export function shownState(stop: Pick<RouteStopSummary, "state" | "arrivalObserved">): RouteStopState {
  return stop.state === "enRoute" && stop.arrivalObserved ? "arrived" : stop.state;
}

/**
 * A route's notes as the lines the vendor stated them in, trimmed, with blank lines dropped; empty when it states none.
 */
export function formatNoteLines(notes: string | null): string[] {
  return (notes ?? "")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
}

/** Miles rounded to the mile with thousands separators, as "1,204". */
function wholeMiles(miles: number): string {
  return Math.round(miles).toLocaleString("en-US");
}

/** A route's miles: what the driver has done against its total. */
export type RouteMiles = { done: number; total: number };

/** The parts of a route summary its miles are read from. */
export type RouteMilesSource = Pick<
  DriverRouteSummary,
  "lifecycle" | "loadedMiles" | "emptyMiles" | "stops" | "unresolvedStopCount"
>;

/**
 * Whether the forecast charges miles to reach a stop of this role.
 *
 * A pickup and a delivery are, and so is a trailer interchange, which the forecast reads as the pickup or the delivery
 * of the leg it sits on; the strip places one by count instead (see {@link endsSpan}). A fuel stop or waypoint never is.
 */
function endsLeg(role: RouteStopSummary["role"]): boolean {
  return role === "pickup" || role === "delivery" || role === "trailerInterchange";
}

/**
 * Whether the forecast has measured every leg still ahead of the driver, so what it charges is all that is left.
 *
 * Not before the route's stops resolve, nor while any is unresolved: a stop missing from the list hides its leg. Nor
 * while a stop ahead that ends a leg (see {@link endsLeg}) has no entry in the forecast (see
 * {@link RouteStopSummary.inForecast}). A stop the driver is past, a fuel stop, and a waypoint leave nothing to wait on.
 */
function legsMeasured(route: Pick<DriverRouteSummary, "stops" | "unresolvedStopCount">): boolean {
  if (route.stops.length === 0 || route.unresolvedStopCount > 0) return false;
  return route.stops.every((stop) => stop.done || !endsLeg(stop.role) || stop.inForecast);
}

/**
 * The route's miles done against its total; null without the order's loaded miles or until every leg is measured.
 *
 * The total is the loaded miles plus any deadhead. What is left is every leg the forecast still charges — passed stops,
 * fuel stops and waypoints carry none — so what is done is the total less that, held inside the total: the clamp
 * absorbs floored legs that lift what is left past the stated miles, and a route whose every leg is driven reads its
 * whole total.
 *
 * A leg the forecast has not measured would count as driven, so nothing is stated until every one ahead is (see
 * {@link legsMeasured}): a route opened before its stops were minted would otherwise read its whole total as done.
 *
 * A route the driver has finished reads its whole total without that wait. Every leg is behind them, measured or not,
 * and a finished route whose stops never resolved would otherwise read nothing. A leg a stale forecast still charges
 * on one is ignored for the same reason.
 */
export function routeMiles(route: RouteMilesSource): RouteMiles | null {
  if (route.loadedMiles === null) return null;
  const total = route.loadedMiles + (route.emptyMiles ?? 0);
  // A finished route has every leg behind it, so there is nothing to wait on and nothing left.
  if (route.lifecycle === "recentlyCompleted") return { done: total, total };
  // A route still being run, or queued, waits until the forecast has measured every leg it has left.
  if (!legsMeasured(route)) return null;
  const left = sumBy(route.stops, (stop) => stop.legMiles ?? 0);
  return { done: clamp(total - left, 0, total), total };
}

/**
 * The route's progress in miles as "400 of 600 mi completed"; null whenever {@link routeMiles} states none.
 */
export function formatMilesCompleted(route: RouteMilesSource): string | null {
  const miles = routeMiles(route);
  if (miles === null) return null;
  return `${wholeMiles(miles.done)} of ${wholeMiles(miles.total)} mi completed`;
}

/**
 * Where the truck icon goes down a route's stops: on the stop the driver has reached and not left, else on the leg to
 * the next stop; null once every stop is done, and on a route that is not running.
 *
 * The driver has reached the next stop when the forecast observed the arrival, and is still at the last done stop when
 * its state is `arrived` — which Platform Science never sends, but other vendors do.
 */
export function truckPlacement(
  route: Pick<DriverRouteSummary, "lifecycle" | "stops">,
): { at: "stop" | "beforeStop"; stopId: string } | null {
  if (route.lifecycle !== "current") return null;
  const nextIndex = route.stops.findIndex((stop) => !stop.done);
  if (nextIndex === -1) return null;
  const next = route.stops[nextIndex];
  if (next.arrivalObserved) return { at: "stop", stopId: next.id };
  const lastDone = nextIndex > 0 ? route.stops[nextIndex - 1] : null;
  if (lastDone?.state === "arrived") return { at: "stop", stopId: lastDone.id };
  return { at: "beforeStop", stopId: next.id };
}

/** One leg of the route as the strip draws it: the stop it ends at, and the share of the strip's width it gets. */
export type RouteLeg = { stop: RouteStopSummary; share: number };

/** A span's share of the strip is held between this many even shares and {@link SPAN_SHARE_CAP} of them. */
const SPAN_SHARE_FLOOR = 0.5;
const SPAN_SHARE_CAP = 3;

/**
 * Whether a stop ends a span of the strip, as a pickup or a delivery does.
 *
 * A trailer interchange, fuel stop, or waypoint rides the span it sits on, placed by count (see {@link routeLegs}),
 * though the forecast charges a trailer interchange a leg of its own (see {@link endsLeg}).
 */
function endsSpan(stop: Pick<RouteStopSummary, "role">): boolean {
  return stop.role === "pickup" || stop.role === "delivery";
}

/**
 * The route's legs with the share of the strip each gets, one per resolved stop in run order, summing to one.
 *
 * The strip is measured span by span, a span running from one pickup or delivery to the next. A span's miles are what
 * the forecast charges before any progress: the deadhead for the first pickup, nothing for a later one (the vendor
 * states no miles between pickups), and an even share of the loaded miles for each delivery. Without the order's
 * loaded miles every measured span counts the same. Each share is then held between half and three times an even share, so a
 * two-mile hop beside a two-hundred-mile run reads shorter without vanishing and one long span cannot crowd the rest
 * off the strip; the held shares are scaled back to sum to one.
 *
 * The trailer interchanges, fuel stops, and waypoints before a pickup or delivery sit on its span, dividing it evenly
 * by count rather than by miles: the vendor never states how far out of the way a fuel stop or waypoint is. Any after
 * the last pickup or delivery share a span of no miles, held at the floor.
 */
export function routeLegs(route: Pick<DriverRouteSummary, "loadedMiles" | "emptyMiles" | "stops">): RouteLeg[] {
  const { stops, loadedMiles } = route;
  if (stops.length === 0) return [];
  const deliveries = stops.filter((stop) => stop.role === "delivery").length;
  const firstPickup = stops.find((stop) => stop.role === "pickup");
  const milesTo = (stop: RouteStopSummary): number => {
    if (loadedMiles === null) return 1;
    if (stop === firstPickup) return route.emptyMiles ?? 0;
    if (stop.role === "delivery") return loadedMiles / deliveries;
    return 0;
  };

  // Group each stop with the pickup or delivery whose span it rides; anything after the last one is a span of no miles.
  const spans: { stops: RouteStopSummary[]; miles: number }[] = [];
  let pending: RouteStopSummary[] = [];
  for (const stop of stops) {
    pending.push(stop);
    if (!endsSpan(stop)) continue;
    spans.push({ stops: pending, miles: milesTo(stop) });
    pending = [];
  }
  if (pending.length > 0) spans.push({ stops: pending, miles: 0 });

  // Size each span by its miles, held inside the floor and the cap.
  const total = sum(spans.map((span) => span.miles));
  const even = 1 / spans.length;
  const held = spans.map((span) =>
    clamp(total > 0 ? span.miles / total : even, even * SPAN_SHARE_FLOOR, even * SPAN_SHARE_CAP),
  );
  const heldTotal = sum(held);
  // Divide each span evenly over the stops on it.
  return spans.flatMap((span, index) =>
    span.stops.map((stop) => ({ stop, share: held[index] / heldTotal / span.stops.length })),
  );
}

/** A leg's miles as "312 mi loaded" or "33 mi empty", rounded to the mile; null when either is missing. */
function formatLegMiles(miles: number | null, kind: RouteStopSummary["legKind"]): string | null {
  if (miles === null || miles === undefined || !kind) return null;
  return `${wholeMiles(miles)} mi ${kind}`;
}

/**
 * The miles of the leg ending at a stop as its times line shows them (see {@link formatLegMiles}); null once the
 * forecast observed the arrival, which zeroes the leg and would otherwise read "0 mi loaded" beside "arrived HH:mm".
 */
export function formatStopLegMiles(
  stop: Pick<RouteStopSummary, "legMiles" | "legKind" | "arrivalObserved">,
): string | null {
  return stop.arrivalObserved ? null : formatLegMiles(stop.legMiles, stop.legKind);
}

/** What each projected rest is called on the PTA block's Factors line. */
const REST_LABELS: Record<RouteRestSummary["type"], string> = {
  break30: "30-minute break",
  reset10: "10-hour reset",
  restart34: "34-hour restart",
};

/** The forecast flag raised when the walk ran on fallback clocks for want of the driver's own. */
const NO_HOS_FLAG: RouteForecast["flags"][number] = "noHos";

/**
 * A span of time as "HH:mm–HH:mm", each end carrying its date when it is not the dispatcher's today; an end on the
 * start's own day states that day once.
 */
function formatSpan(startsAt: string, endsAt: string): string {
  const start = formatItemTime(startsAt)?.short ?? "";
  const end = DateTime.fromJSDate(new Date(startsAt)).hasSame(DateTime.fromJSDate(new Date(endsAt)), "day")
    ? formatLocalDate(new Date(endsAt), "HH:mm")
    : (formatItemTime(endsAt)?.short ?? "");
  return `${start}–${end}`;
}

/** A projected rest as the Factors line reads it: "10-hour reset 22:10–Sep 25, 08:10", with "in progress" when it is. */
export function formatRest(rest: RouteRestSummary): string {
  const words = `${REST_LABELS[rest.type]} ${formatSpan(rest.startsAt, rest.endsAt)}`;
  return rest.inProgress ? `${words} · in progress` : words;
}

/**
 * The PTA block's Factors, a line each: every HOS rest the forecast projects before the PTA, or that none is needed,
 * then a note when the walk ran on assumed clocks. Null when the forecast did not walk the driver's hours of service,
 * so a team route, a finished one, or one without a forecast shows no Factors at all.
 */
export function formatFactors(
  route: Pick<DriverRouteSummary, "rests" | "hosInForecast" | "forecastFlags">,
): string[] | null {
  if (!route.hosInForecast) return null;
  const rests = route.rests.length === 0 ? ["No HOS rest needed"] : route.rests.map(formatRest);
  if (!route.forecastFlags.includes(NO_HOS_FLAG)) return rests;
  return [...rests, "No HOS data for this driver; the forecast assumed their clocks"];
}
